import React, { useState } from 'react';
import axios from 'axios';
import { useToken } from '../../hooks/useToken';
import Spinner from '../common/Spinner';
import { getConfig } from '../../config/config-helper';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchEventDetails, selectRequestStatus } from '../../reducers/eventsSlice';
import { useCurrentUser } from '../../hooks/useCurrentUser';

interface Props {
  eventSlug: string;
  instructorEmail: string;
  className?: string;
}

export default function EventRemoveInstructorButton({ eventSlug, instructorEmail, className = '' }: Props) {
  const dispatch = useAppDispatch();
  const token = useToken();
  const { canPublish } = useCurrentUser();
  const role = canPublish ? 'admin' : 'consumer';
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestStatus = useAppSelector(selectRequestStatus);

  const { dataopsAdminAppApiEndpoint } = getConfig();

  async function removeInstructorHandler() {
    setRemoving(true);
    setError(null);

    const url = `${dataopsAdminAppApiEndpoint}/event_management/${eventSlug}/instructors/remove`;
    try {
      await axios.post(
        url,
        { email: instructorEmail },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Refresh event data
      dispatch(fetchEventDetails({ token, role, slug: eventSlug })).catch((err: unknown) => err);
    } catch (err: any) {
      console.log('Failed to remove instructor: ', err);

      // Get the message_code from the API response
      const messageCode = err.response?.data?.message_code;
      let errorMessage = err.response?.data?.message || 'Failed to remove instructor';

      console.log('Error message code:', messageCode);
      // Customize error message based on message_code
      switch (messageCode) {
        case 'ERR_USER_NOT_MEMBER':
          errorMessage = 'You are not registered as a member in the system.';
          break;
        case 'ERR_EVENT_NOT_FOUND':
          errorMessage = 'The specified event could not be found.';
          break;
        case 'ERR_INTERNAL_SERVER_ERROR':
          errorMessage = 'An internal server error occurred. Please try again later.';
          break;
        case 'ERR_NOT_AUTHORIZED':
          errorMessage = 'You are not authorized to remove instructors from this event.';
          break;
        case 'INVALID_EMAIL':
          errorMessage = 'Invalid email format.';
          break;
        case 'INSTRUCTOR_NOT_FOUND':
          errorMessage = 'Instructor not found for this event.';
          break;
        default:
          // Use the original message from the server
          break;
      }
      if (err.code === 'ERR_NETWORK') {
        errorMessage = 'Network error. Please check your internet connection.';
      }
      setError(errorMessage);
    } finally {
      setRemoving(false);
    }
  }

  return (
    <button
      className={`inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 hover:bg-red-100 text-gray-500 hover:text-red-500 focus:outline-none transition-colors ${className}`}
      onClick={() => {
        removeInstructorHandler().catch(() => {
          console.error('Error removing instructor:', instructorEmail);
        });
      }}
      disabled={removing || requestStatus === 'loading'}
      title={`Remove ${instructorEmail} as instructor`}
      aria-label={`Remove ${instructorEmail} as instructor`}
      data-testid="remove-instructor-button"
    >
      {removing ? (
        <Spinner size="small" />
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      )}
      {error && <span className="sr-only">{error}</span>}
    </button>
  );
}
